// @givmo/compliance — "What goes with it": the filing's attachment checklist, on the workbench
// (CCM-R1-DOCS; slice 2 §5.5, frames 31 and 33; the GO's Q4).
//
// Each row is one item the rule's pinned version records, in the catalog's words, with its
// condition and, where the agency says something beside it, the agency's own words (Maryland's
// Schedule B sentence, D-CCM-24 J3 (A)). Where it stands is the server's: attached (the files, who
// chose each and since when it is in Documents), not attached yet, or not needed for this filing
// as the organization marked it. Givmo never infers that an item is not needed.
//
// The acts (Q4): Choose from Documents opens a sheet listing Documents in the approved columns,
// several at once, and Attach applies the difference — attaching what was checked, removing what
// was unchecked; Upload puts a new PDF in Documents and attaches it; Remove takes the row's files
// off; "Not needed for this filing" and Undo record the organization's choice.
//
// "See the checklist (page 2)" sits beside the heading where the form serves the page that holds its
// checklist (CCM-R1-DOCS-PRINTED-WEB; frames 31, 32, 61): it shows that page in the column beside.

import { useRef, useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  Checkbox,
  FOCUS_RING,
  Icon,
  Sheet,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
  cn,
  testIdProp,
  toast,
} from '@givmo/design-system';
import { FileText, Upload } from 'lucide-react';
import { useCompliance } from './compliance-context';
import {
  displayLocalDate,
  extractArtifacts,
  extractObligationArtifacts,
  serverMessage,
  type ComplianceArtifact,
  type ObligationArtifact,
} from './compliance-decode';
import { documentUses } from './compliance-documents-model';
import { useFilingsComposition } from './compliance-filings-reads';
import { DOCUMENT_ROLE_NAMES } from './compliance-panel-model';
import {
  decodeChecklist,
  decodeVersionDownloadUrl,
  type ChecklistDocument,
  type ChecklistItem,
} from './compliance-preparation-decode';
import {
  ADDED_COLUMN,
  ATTACH,
  CANCEL,
  CHANGE,
  CHOOSE_FROM_DOCUMENTS,
  DOCUMENT_COLUMN,
  NOT_ATTACHED_YET,
  NOT_NEEDED_FOR_THIS_FILING,
  REMOVE,
  UNDO,
  UPLOAD,
  USED_FOR_COLUMN,
  WHAT_GOES_WITH_IT,
  checklistLead,
  chosenFileLine,
  instructionLine,
  notNeededLine,
  seeTheChecklistLine,
} from './compliance-preparation-model';
import { filingChecklistQueryKey, listAllArtifacts } from './compliance-queries';
import { uploadArtifactFile, uploadRefusalMessage } from './artifact-upload';
import { QuietButton } from './filing-workbench-pages';

const PREFIX = 'compliance-workbench-checklist';

/** The failure line the panel's record forms use. */
const RECORD_FAILED = 'We couldn’t record that. Please try again.';

type Act = { kind: 'remove'; documentId: string } | { kind: 'not_needed' } | { kind: 'needed' };

function useChecklistWrites(caseId: string) {
  const { orgRef, operations } = useCompliance();
  const queryClient = useQueryClient();
  const settle = (body: unknown) => {
    // Each write answers the checklist as it now stands.
    if (decodeChecklist(body).length > 0) {
      queryClient.setQueryData(filingChecklistQueryKey(orgRef, caseId), body);
    } else {
      void queryClient.invalidateQueries({ queryKey: filingChecklistQueryKey(orgRef, caseId) });
    }
  };
  const attach = (attachmentIndex: number, artifactId: string) =>
    operations
      .attachChecklistDocument(orgRef ?? '', caseId, attachmentIndex, {
        source_artifact_id: artifactId,
      })
      .then(settle);
  const act = (attachmentIndex: number, done: Act) =>
    operations
      .recordChecklistAct(
        orgRef ?? '',
        caseId,
        attachmentIndex,
        done.kind === 'remove'
          ? { act: 'remove', document_id: done.documentId }
          : { act: done.kind },
      )
      .then(settle);
  return { attach, act };
}

/** The section: its lead, then one row per checklist item. */
export function WorkbenchChecklist({
  caseId,
  items,
  jurisdiction,
  editable,
  checklistPage,
  onShowChecklistPage,
}: {
  caseId: string;
  items: readonly ChecklistItem[];
  jurisdiction: string;
  editable: boolean;
  /** The page of the agency's document holding the checklist, as the form serves it. */
  checklistPage?: number;
  onShowChecklistPage?: (page: number) => void;
}) {
  const { orgRef, operations, isReady, moduleStanding, personName } = useCompliance();
  const enabled = isReady && moduleStanding === 'available' && orgRef !== null;
  const vault = useQuery({
    queryKey: ['compliance', 'artifacts', orgRef],
    queryFn: () => listAllArtifacts(operations, orgRef ?? ''),
    enabled,
  });
  const artifacts = extractArtifacts(vault.data);
  const [choosing, setChoosing] = useState<ChecklistItem | null>(null);

  return (
    <section className="flex flex-col gap-10" {...testIdProp(PREFIX)}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-12 gap-y-4">
        <h2 className="text-s14 font-w600 text-text-strong">{WHAT_GOES_WITH_IT}</h2>
        {checklistPage !== undefined && onShowChecklistPage !== undefined && (
          <QuietButton
            onClick={() => onShowChecklistPage(checklistPage)}
            testId={`${PREFIX}-see-page`}
          >
            {seeTheChecklistLine(checklistPage)}
          </QuietButton>
        )}
      </div>
      <p className="-mt-4 text-s13 text-text-secondary">{checklistLead(jurisdiction)}</p>
      <Card className="overflow-hidden">
        <ul className="divide-y divide-border-subtle">
          {items.map((item) => (
            <ChecklistRow
              key={item.attachmentIndex}
              caseId={caseId}
              item={item}
              jurisdiction={jurisdiction}
              editable={editable}
              artifacts={artifacts}
              personName={personName}
              onChoose={() => setChoosing(item)}
            />
          ))}
        </ul>
      </Card>
      {choosing !== null && (
        <ChooseFromDocuments
          caseId={caseId}
          item={choosing}
          artifacts={artifacts}
          onClose={() => setChoosing(null)}
        />
      )}
    </section>
  );
}

function ChecklistRow({
  caseId,
  item,
  jurisdiction,
  editable,
  artifacts,
  personName,
  onChoose,
}: {
  caseId: string;
  item: ChecklistItem;
  jurisdiction: string;
  editable: boolean;
  artifacts: readonly ComplianceArtifact[];
  personName?: (userId: string) => string | undefined;
  onChoose: () => void;
}) {
  const { orgRef, operations } = useCompliance();
  const { attach, act } = useChecklistWrites(caseId);
  const fileInput = useRef<HTMLInputElement>(null);
  const write = useMutation({
    mutationFn: (done: Act) => act(item.attachmentIndex, done),
    onError: (cause) => toast.error(serverMessage(cause, RECORD_FAILED)),
  });
  const removeAll = useMutation({
    mutationFn: async () => {
      for (const document of item.documents) {
        await act(item.attachmentIndex, { kind: 'remove', documentId: document.documentId });
      }
    },
    onError: (cause) =>
      toast.error(serverMessage(cause, 'We couldn’t remove that. Please try again.')),
  });
  const upload = useMutation({
    mutationFn: async (file: File) => {
      const artifact = await uploadArtifactFile(operations, orgRef, file, 'application/pdf');
      await attach(item.attachmentIndex, artifact.artifactId);
    },
    onError: (cause) => toast.error(serverMessage(cause, uploadRefusalMessage(cause))),
  });
  const busy = write.isPending || removeAll.isPending || upload.isPending;

  return (
    <li
      className="flex flex-col gap-6 px-20 py-14"
      {...testIdProp(`${PREFIX}-${item.attachmentIndex}`)}
    >
      <p className="text-s14 leading-h15 text-text">{item.item}</p>
      {item.condition !== undefined && (
        <p className="text-s12 leading-h15 text-text-tertiary">{item.condition}</p>
      )}
      {item.agencyQuote !== undefined && (
        <p
          className="text-s12 leading-h15 text-text-tertiary"
          {...testIdProp(`${PREFIX}-${item.attachmentIndex}-quote`)}
        >
          {instructionLine(jurisdiction, item.agencyQuote.text)}
        </p>
      )}

      {item.status === 'attached' &&
        item.documents.map((document) => {
          const source = artifacts.find(
            (artifact) => artifact.artifactId === document.latestVersion?.sourceArtifactId,
          );
          return (
            <div
              key={document.documentId}
              className="flex flex-wrap items-center gap-x-10 gap-y-2"
              {...testIdProp(`${PREFIX}-${item.attachmentIndex}-file`)}
            >
              <Icon icon={FileText} size={15} className="shrink-0 text-icon-muted" aria-hidden />
              <AttachedFileName document={document} />
              <span className="shrink-0 text-s12 text-text-tertiary">
                {chosenFileLine(document.latestVersion, source?.uploadedAt, personName)}
              </span>
            </div>
          );
        })}
      {item.status === 'attached' && editable && (
        <div className="flex gap-16">
          <QuietButton onClick={onChoose} testId={`${PREFIX}-${item.attachmentIndex}-change`}>
            {CHANGE}
          </QuietButton>
          <QuietButton
            onClick={() => removeAll.mutate()}
            testId={`${PREFIX}-${item.attachmentIndex}-remove`}
          >
            {REMOVE}
          </QuietButton>
        </div>
      )}

      {item.status === 'open' && (
        <div className="flex flex-wrap items-center gap-8">
          <span className="mr-4 text-s13 font-w600 text-text">{NOT_ATTACHED_YET}</span>
          {editable && (
            <>
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                disabled={busy}
                onClick={onChoose}
                testId={`${PREFIX}-${item.attachmentIndex}-choose`}
              >
                {CHOOSE_FROM_DOCUMENTS}
              </Button>
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                disabled={busy}
                loading={upload.isPending}
                leadingIcon={<Icon icon={Upload} size={14} aria-hidden />}
                onClick={() => fileInput.current?.click()}
                testId={`${PREFIX}-${item.attachmentIndex}-upload`}
              >
                {UPLOAD}
              </Button>
              <input
                ref={fileInput}
                type="file"
                accept="application/pdf,.pdf"
                className="hidden"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.target.value = '';
                  if (file !== undefined) upload.mutate(file);
                }}
                {...testIdProp(`${PREFIX}-${item.attachmentIndex}-file-input`)}
              />
              <QuietButton
                onClick={() => write.mutate({ kind: 'not_needed' })}
                testId={`${PREFIX}-${item.attachmentIndex}-not-needed`}
              >
                {NOT_NEEDED_FOR_THIS_FILING}
              </QuietButton>
            </>
          )}
        </div>
      )}

      {item.status === 'not_needed' && (
        <p
          className="text-s13 text-text-secondary"
          {...testIdProp(`${PREFIX}-${item.attachmentIndex}-not-needed-line`)}
        >
          {notNeededLine(item, personName)}
          {editable && (
            <>
              {' · '}
              <QuietButton
                onClick={() => write.mutate({ kind: 'needed' })}
                testId={`${PREFIX}-${item.attachmentIndex}-undo`}
              >
                {UNDO}
              </QuietButton>
            </>
          )}
        </p>
      )}
    </li>
  );
}

/**
 * Choose from Documents (frame 33): Documents in the approved columns, the row's item as the
 * subtitle, several at once. Attach applies the difference from what the item holds.
 */
const OPEN_FAILED = 'We couldn’t open that document. Please try again.';

/**
 * An attached file's name, which opens it (slice 2's design note: "The file's name (opens it)"):
 * the filing's own copy, through the same logged version download Download uses. Downloaded, never
 * previewed, and the link is minted per click and never held.
 */
function AttachedFileName({ document }: { document: ChecklistDocument }) {
  const { orgRef, operations } = useCompliance();
  const version = document.latestVersion;
  const open = useMutation({
    mutationFn: async () => {
      if (orgRef === null || version === undefined) throw new Error(OPEN_FAILED);
      const url = decodeVersionDownloadUrl(
        await operations.getDocumentVersionDownload(orgRef, document.documentId, version.versionId),
      );
      if (url === undefined) throw new Error(OPEN_FAILED);
      return url;
    },
    onSuccess: (url) => window.location.assign(url),
    onError: (cause) => toast.error(serverMessage(cause, OPEN_FAILED)),
  });
  if (version === undefined) {
    return <span className="min-w-0 truncate text-s13 text-text">{document.title}</span>;
  }
  return (
    <button
      type="button"
      onClick={() => open.mutate()}
      className={cn(
        'min-w-0 truncate rounded-xs text-left text-s13 text-text underline decoration-border-strong underline-offset-2',
        FOCUS_RING,
      )}
      {...testIdProp(`${PREFIX}-document-${document.documentId}`)}
    >
      {document.title}
    </button>
  );
}

function ChooseFromDocuments({
  caseId,
  item,
  artifacts,
  onClose,
}: {
  caseId: string;
  item: ChecklistItem;
  artifacts: readonly ComplianceArtifact[];
  onClose: () => void;
}) {
  const { orgRef, operations, personName } = useCompliance();
  const { attach, act } = useChecklistWrites(caseId);
  // What the item holds now, by the Documents entry each file was registered from.
  const held = new Map(
    item.documents.flatMap((document) =>
      document.latestVersion?.sourceArtifactId !== undefined
        ? [[document.latestVersion.sourceArtifactId, document.documentId] as const]
        : [],
    ),
  );
  const [picked, setPicked] = useState<ReadonlySet<string>>(() => new Set(held.keys()));
  const toggle = (artifactId: string) =>
    setPicked((current) => {
      const next = new Set(current);
      if (next.has(artifactId)) next.delete(artifactId);
      else next.add(artifactId);
      return next;
    });

  // "Used for", as Documents shows it.
  const composition = useFilingsComposition(operations, orgRef);
  const libraryIds = (composition.rows ?? [])
    .filter((row) => row.ref.kind === 'library')
    .map((row) => row.ref.id);
  const obligationDocuments = useQueries({
    queries: libraryIds.map((obligationId) => ({
      queryKey: ['compliance', 'obligation-artifacts', orgRef, obligationId],
      queryFn: () => operations.listObligationArtifacts(orgRef ?? '', obligationId),
      enabled: orgRef !== null,
    })),
  });
  const uses = documentUses({
    rows: composition.rows ?? [],
    cycles: composition.records?.cycles ?? [],
    obligationDocuments: new Map<string, readonly ObligationArtifact[]>(
      libraryIds.map((obligationId, index) => [
        obligationId,
        extractObligationArtifacts(obligationDocuments[index]?.data),
      ]),
    ),
    ownFilingCycles: new Map(
      [...(composition.records?.ownFilingDetails ?? new Map())].map(([filingId, detail]) => [
        filingId,
        detail.cycles,
      ]),
    ),
  });

  const apply = useMutation({
    mutationFn: async () => {
      for (const artifactId of picked) {
        if (!held.has(artifactId)) await attach(item.attachmentIndex, artifactId);
      }
      for (const [artifactId, documentId] of held) {
        if (!picked.has(artifactId)) {
          await act(item.attachmentIndex, { kind: 'remove', documentId });
        }
      }
    },
    onSuccess: onClose,
    onError: (cause) => toast.error(serverMessage(cause, RECORD_FAILED)),
  });
  const changed =
    [...picked].some((artifactId) => !held.has(artifactId)) ||
    [...held.keys()].some((artifactId) => !picked.has(artifactId));
  // Newest first, as Documents lists them.
  const rows = [...artifacts].sort((a, b) =>
    (b.uploadedAt ?? '').localeCompare(a.uploadedAt ?? ''),
  );

  return (
    <Sheet.Root open modal onOpenChange={(open) => (!open ? onClose() : undefined)}>
      <Sheet.Content size="lg" initialFocus={false} testId={`${PREFIX}-choose`}>
        <Sheet.Header>
          <div className="flex min-w-0 flex-col gap-4">
            <Sheet.Title className="font-sans text-s18 font-w600 tracking-givmo text-text-strong">
              {CHOOSE_FROM_DOCUMENTS}
            </Sheet.Title>
            <p className="text-s13 leading-h15 text-text-secondary">{item.item}</p>
          </div>
        </Sheet.Header>
        <Sheet.Body className="flex flex-col gap-16">
          <Card className="overflow-hidden">
            <Table className="table-fixed">
              <THead>
                <TR>
                  <TH className="w-[44%] max-sm:w-full">{DOCUMENT_COLUMN}</TH>
                  <TH className="w-[22%] max-sm:hidden">{ADDED_COLUMN}</TH>
                  <TH className="max-sm:hidden">{USED_FOR_COLUMN}</TH>
                </TR>
              </THead>
              <TBody>
                {rows.map((artifact) => {
                  const checked = picked.has(artifact.artifactId);
                  const addedBy =
                    artifact.uploadedByUserId !== undefined
                      ? personName?.(artifact.uploadedByUserId)
                      : undefined;
                  const documentUsesFor = uses.get(artifact.artifactId) ?? [];
                  const usedFor = documentUsesFor
                    .map((use) => `${use.title} · ${DOCUMENT_ROLE_NAMES[use.role]}`)
                    .join(', ');
                  return (
                    <TR
                      key={artifact.artifactId}
                      className={cn('cursor-pointer', checked && 'bg-brand-50/60')}
                      onClick={() => toggle(artifact.artifactId)}
                      {...testIdProp(`${PREFIX}-choose-${artifact.artifactId}`)}
                    >
                      <TD>
                        <div className="flex min-w-0 items-center gap-8 max-sm:items-start">
                          <span onClick={(event) => event.stopPropagation()}>
                            <Checkbox
                              checked={checked}
                              onCheckedChange={() => toggle(artifact.artifactId)}
                              aria-label={artifact.fileName}
                            />
                          </span>
                          <div className="flex min-w-0 flex-col gap-2">
                            <span className="truncate text-s13 text-text max-sm:whitespace-normal max-sm:break-words">
                              {artifact.fileName}
                            </span>
                            {/* Below sm the row stacks as Documents' rows do: a phone has no room
                                for three columns, and a name cut to "Board list No…" can't be told
                                from its neighbour. The two columns' facts go under the name. */}
                            <span className="hidden flex-wrap gap-x-12 text-s12 text-text-tertiary max-sm:flex">
                              <span>{`${ADDED_COLUMN} ${displayLocalDate(artifact.uploadedAt)}`}</span>
                              {addedBy !== undefined && <span>{`Added by ${addedBy}`}</span>}
                            </span>
                            {usedFor.length > 0 && (
                              <span className="hidden text-s12 leading-h15 text-text-secondary max-sm:block">
                                {`${USED_FOR_COLUMN} ${usedFor}`}
                              </span>
                            )}
                          </div>
                        </div>
                      </TD>
                      <TD className="max-sm:hidden">
                        <div className="flex flex-col">
                          <span className="text-s13 text-text">
                            {displayLocalDate(artifact.uploadedAt)}
                          </span>
                          {addedBy !== undefined && (
                            <span className="truncate text-s12 text-text-tertiary">{addedBy}</span>
                          )}
                        </div>
                      </TD>
                      <TD className="max-sm:hidden">
                        <span className="text-s12 leading-h15 text-text-secondary">
                          {usedFor.length === 0 ? '—' : usedFor}
                        </span>
                      </TD>
                    </TR>
                  );
                })}
              </TBody>
            </Table>
          </Card>
        </Sheet.Body>
        <Sheet.Footer>
          <div className="flex gap-8">
            <Button
              appearance="saas"
              tone="primary"
              size="md"
              disabled={!changed}
              loading={apply.isPending}
              onClick={() => apply.mutate()}
              testId={`${PREFIX}-choose-attach`}
            >
              {ATTACH}
            </Button>
            <Button appearance="saas" tone="ghost" size="md" onClick={onClose}>
              {CANCEL}
            </Button>
          </div>
        </Sheet.Footer>
      </Sheet.Content>
    </Sheet.Root>
  );
}
